import { AlertTriangle, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { FeedbackAudioKey } from '../hooks/useFeedbackAssistant';

interface DeleteClipDialogProps {
  open: boolean;
  clipIndex: number;
  onConfirm: () => void;
  onCancel: () => void;
  playAudio?: (key: FeedbackAudioKey) => Promise<void>;
}

export function DeleteClipDialog({
  open,
  clipIndex,
  onConfirm,
  onCancel,
  playAudio,
}: DeleteClipDialogProps) {
  const { t } = useTranslation();
  // Play clip_delete_confirm when dialog opens (spec §8.9)
  useEffect(() => {
    if (open) playAudio?.('clip_delete_confirm');
  }, [open, playAudio]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
      onClick={onCancel}
      onKeyDown={e => e.key === 'Escape' && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-label={t('Feedback.clipDelete.title')}
    >
      <div
        className="mx-4 w-full max-w-sm rounded-xl bg-background p-6 shadow-lg"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-destructive shrink-0" />
          <p className="text-base font-medium">
            {t('Feedback.clipDelete.title', { index: clipIndex + 1 })}
          </p>
        </div>

        <p className="text-sm text-muted-foreground mb-6">{t('Feedback.clipDelete.description')}</p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-lg border border-border px-4 py-3 text-sm font-medium transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {t('Feedback.clipDelete.keep')}
          </button>

          <button
            type="button"
            onClick={onConfirm}
            className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-[#f9594c] px-4 py-3 text-sm font-medium text-white transition-colors hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-destructive"
          >
            <Trash2 className="h-4 w-4" />
            {t('Feedback.clipDelete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
